import React, { useState, useEffect } from 'react';
import { Stack, Text, Group, Progress, Badge, Button, Paper } from '@mantine/core';
import {
  CheckSquare,
  StickyNote,
  Calendar,
  Kanban,
  Users,
  CheckCircle,
  X
} from 'lucide-react';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useDashboardData } from '../../../../hooks/dashboard';
import { hasVisited } from '../../../../utils/pageVisitTracker';

interface OnboardingTask {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  completed: boolean;
  navigationTarget?: 'todos' | 'notes' | 'calendar' | 'kanban';
}

const ONBOARDING_TASKS: OnboardingTask[] = [
  {
    id: 'add-first-task',
    title: 'Add your first task',
    description: 'Add a task to get started with Symphony Todo',
    icon: CheckSquare,
    completed: false,
    navigationTarget: 'todos'
  },
  {
    id: 'create-note',
    title: 'Create a note',
    description: 'Jot down quick thoughts, ideas, or meeting notes',
    icon: StickyNote,
    completed: false,
    navigationTarget: 'notes'
  },
  {
    id: 'invite-collaborator',
    title: 'Invite a collaborator',
    description: 'Share a list with a team member or friend',
    icon: Users,
    completed: false,
    navigationTarget: 'todos'
  },
  {
    id: 'set-due-date',
    title: 'Set a due date',
    description: 'Add a deadline to one of your tasks',
    icon: Calendar,
    completed: false,
    navigationTarget: 'todos'
  },
  {
    id: 'explore-premium',
    title: 'Explore premium features',
    description: 'Check out Calendar and Kanban views for advanced planning',
    icon: Kanban,
    completed: false,
    navigationTarget: 'calendar'
  }
];

const STORAGE_KEY = 'symphonytodo-onboarding-progress';
const DISMISSED_KEY = 'symphonytodo-onboarding-dismissed';

// Utility function to check if onboarding has been dismissed
export const isOnboardingDismissed = (): boolean => {
  return localStorage.getItem(DISMISSED_KEY) === 'true';
};

interface OnboardingTasksProps {
  onDismiss?: () => void;
}

export const OnboardingTasks: React.FC<OnboardingTasksProps> = ({ onDismiss }) => {
  const { setCurrentMode } = useNavigation();
  const dashboardData = useDashboardData();
  const [visitedSubscriptionPlan, setVisitedSubscriptionPlan] = useState(hasVisited.subscriptionPlan());
  const [isDismissed, setIsDismissed] = useState(false);

  // Check real user data for automatic completion detection
  const checkAutoCompletion = (taskId: string): boolean => {
    switch (taskId) {
      case 'add-first-task':
        return dashboardData.todos.length > 0;
      case 'create-note':
        return dashboardData.notes.length > 0;
      case 'invite-collaborator':
        return dashboardData.todoLists.some(list => list.sharedWith && list.sharedWith.length > 0);
      case 'set-due-date':
        return dashboardData.todos.some(todo => todo.dueDate);
      case 'explore-premium':
        return visitedSubscriptionPlan;
      default:
        return false;
    }
  };

  const [tasks, setTasks] = useState<OnboardingTask[]>(() => {
    // Load completed state from localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    const manuallyCompleted = saved ? JSON.parse(saved) as string[] : [];

    return ONBOARDING_TASKS.map(task => ({
      ...task,
      completed: manuallyCompleted.includes(task.id) || checkAutoCompletion(task.id)
    }));
  });

  // Update tasks when dashboard data changes to detect new completions
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const manuallyCompleted = saved ? JSON.parse(saved) as string[] : [];

    setTasks(prevTasks =>
      prevTasks.map(task => {
        const wasManuallyCompleted = manuallyCompleted.includes(task.id);
        const isAutoCompleted = checkAutoCompletion(task.id);
        const newCompletedState = wasManuallyCompleted || isAutoCompleted;

        // If task is newly auto-completed, save it to localStorage
        if (isAutoCompleted && !wasManuallyCompleted && !task.completed) {
          const updatedCompleted = [...manuallyCompleted, task.id];
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedCompleted));
        }

        return {
          ...task,
          completed: newCompletedState
        };
      })
    );
  }, [dashboardData.todos.length, dashboardData.notes.length, dashboardData.todoLists, visitedSubscriptionPlan]);

  // Periodically check if user has visited subscription plan page
  useEffect(() => {
    const checkVisits = () => {
      const hasVisitedPlan = hasVisited.subscriptionPlan();
      if (hasVisitedPlan && !visitedSubscriptionPlan) {
        setVisitedSubscriptionPlan(true);
      }
    };

    // Check immediately and then every few seconds
    checkVisits();
    const interval = setInterval(checkVisits, 3000);

    return () => clearInterval(interval);
  }, [visitedSubscriptionPlan]);

  const completedCount = tasks.filter(task => task.completed).length;
  const progressPercentage = Math.round((completedCount / tasks.length) * 100);
  const allTasksCompleted = completedCount === tasks.length;

  // Debug logging for completion state
  useEffect(() => {
    console.log('Onboarding Debug:', {
      completedCount,
      totalTasks: tasks.length,
      allTasksCompleted,
      tasksStatus: tasks.map(t => ({ id: t.id, completed: t.completed }))
    });
  }, [completedCount, tasks.length, allTasksCompleted]);

  // Save to localStorage whenever tasks change
  useEffect(() => {
    const completedIds = tasks.filter(task => task.completed).map(task => task.id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completedIds));
  }, [tasks]);

  const handleTaskClick = (task: OnboardingTask) => {
    // Only allow navigation for incomplete tasks
    if (!task.completed && task.navigationTarget) {
      setCurrentMode(task.navigationTarget);
    }
  };

  const handleDismissOnboarding = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setIsDismissed(true);
    // Notify parent component to update visibility
    onDismiss?.();
  };

  // Don't render if dismissed (component will fade out)
  if (isDismissed) {
    return null;
  }

  return (
    <Stack gap="md">
      {/* Progress Header */}
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600}>Getting Started</Text>
          <Badge size="sm" variant="light" color="blue">
            {completedCount}/{tasks.length}
          </Badge>
        </Group>
        <Progress
          value={progressPercentage}
          size="sm"
          radius="sm"
          color="blue"
          bg="var(--mantine-color-gray-2)"
        />
        <Text size="xs" c="dimmed">
          {allTasksCompleted
            ? '🎉 Congratulations! You\'ve completed the getting started guide!'
            : `${progressPercentage}% complete - ${tasks.length - completedCount} tasks remaining`
          }
        </Text>
      </Stack>

      {/* Task List */}
      <Stack gap="xs">
        {tasks.map((task) => {
          const Icon = task.icon;
          return (
            <div
              key={task.id}
              className={`onboarding-task ${task.completed ? 'completed' : 'incomplete'}`}
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                padding: '10px 12px',
                borderRadius: '6px',
                backgroundColor: task.completed
                  ? 'var(--mantine-color-green-0)'
                  : 'var(--app-surface)',
                border: task.completed
                  ? '1px solid var(--mantine-color-green-3)'
                  : '1px solid var(--app-border)',
                cursor: task.completed ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: task.completed ? 0.8 : 1
              }}
              onClick={() => handleTaskClick(task)}
              onMouseEnter={(e) => {
                if (!task.completed) {
                  e.currentTarget.style.backgroundColor = 'var(--app-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!task.completed) {
                  e.currentTarget.style.backgroundColor = 'var(--app-surface)';
                }
              }}
            >

              <div style={{
                flexShrink: 0,
                marginTop: '2px',
                display: 'flex',
                alignItems: 'flex-start',
                height: 'auto'
              }}>
                <Icon
                  size={16}
                  style={{
                    color: task.completed
                      ? 'var(--mantine-color-green-6)'
                      : 'var(--app-text-secondary)'
                  }}
                />
              </div>

              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={500}
                  style={{
                    textDecoration: task.completed ? 'line-through' : 'none',
                    color: task.completed
                      ? 'var(--mantine-color-green-7)'
                      : 'var(--app-text-primary)'
                  }}
                >
                  {task.title}
                </Text>
                <Text
                  size="xs"
                  c="dimmed"
                  style={{
                    lineHeight: 1.3,
                    textDecoration: task.completed ? 'line-through' : 'none'
                  }}
                >
                  {task.description}
                </Text>
              </Stack>

              {task.completed && (
                <div style={{
                  flexShrink: 0,
                  marginTop: '2px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  height: 'auto'
                }}>
                  <CheckCircle
                    size={20}
                    style={{
                      color: 'var(--mantine-color-green-6)',
                      fill: 'var(--mantine-color-green-1)'
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </Stack>

      {/* Completion Message - Always visible when all tasks complete */}
      {allTasksCompleted && (
        <Paper
          p="lg"
          withBorder
          style={{
            backgroundColor: 'var(--mantine-color-green-0)',
            borderColor: 'var(--mantine-color-green-3)',
            borderWidth: '2px',
            marginTop: '8px',
            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Stack gap="md">
            <Text
              size="md"
              fw={700}
              style={{ color: 'var(--mantine-color-green-7)' }}
            >
              🎉 Congratulations! You've completed getting started!
            </Text>
            <Text
              size="sm"
              style={{
                color: 'var(--app-text-primary)',
                lineHeight: 1.5
              }}
            >
              Hope you enjoy using SymphonyTodo. If you have any feedback, please use the button in the side panel "Help & Feedback". I would love to hear from you!
            </Text>
            <Group justify="flex-end">
              <Button
                size="md"
                variant="light"
                color="green"
                leftSection={<X size={16} />}
                onClick={handleDismissOnboarding}
                style={{
                  fontWeight: 600
                }}
              >
                Close Getting Started
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};